/**
 * Embed context
 * =============
 * Lets step components report check failures to the embed host without
 * changing their props. Outside /sdk/embed the context value is null, so
 * the hosted /sdk/verify flow renders and behaves exactly as before.
 */

import { createContext, useContext, useEffect, useRef } from 'react';

export const SDKEmbedContext = createContext(null);

export function useSDKEmbed() {
  return useContext(SDKEmbedContext);
}

/**
 * Report a step error to the host once per distinct message.
 * No-op when the SDK is not embedded.
 */
export function useReportStepError(stepKey, error, retryable = true) {
  const embed = useContext(SDKEmbedContext);
  const lastRef = useRef(null);

  useEffect(() => {
    if (!embed || !error) {
      lastRef.current = null;
      return;
    }
    const message = typeof error === 'string' ? error : error?.message || 'Step failed';
    if (lastRef.current === message) return;
    lastRef.current = message;
    embed.reportError({ code: `${stepKey}_failed`, message, retryable });
  }, [embed, error, stepKey, retryable]);
}
